import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import PreferenceGroup from './PreferenceGroup';
import AdditionalPreferencesSection from './AdditionalPreferencesSection';
import Divider from './Divider';
import Label from './Label';
import i18n from '../i18n';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
// Interpreter Preferences Constants
// (Moved from ../types/preferences.js - only used by this component)

/**
 * Interpreter Gender Options
 * Used for the "does not matter" default value
 * Actual gender skills (female/male) come from backend
 */
const InterpreterGender = {
  DOES_NOT_MATTER: 'does_not_matter',
};

/**
 * On-site Fallback Handling Options (On-site bookings only)
 */
const OnsiteFallbackHandling = {
  ASSIGN_REMOTE: 'assign_remote_interpreter',
  CANCEL_BOOKING: 'cancel_booking',
};

/**
 * Default preference values
 */
const defaultPreferences = {
  gender: InterpreterGender.DOES_NOT_MATTER,
  competenceLevels: [], // Array of level IDs from backend
  experienceSkill: 'does_not_matter_experience', // Single skill ID for experience
  onsiteFallbackHandling: OnsiteFallbackHandling.ASSIGN_REMOTE,
  acceptBeyondKmLimit: false, // boolean: true/false
  kmBeyondLimit: undefined, // number: how many kilometers beyond contract distance limit
  acceptAlternativeLanguage: false, // boolean: true/false - only shown when alternative languages exist
  // Soft limit preferences
  backupCallTypeForOnSite: 'call', // 'call' or 'data' - connection type for remote backup
  preferredInterpreters: [],
  excludedInterpreters: [],
  additionalPreferencesExpanded: false,
};

/**
 * Validation error messages
 */
const validationMessages = {
  genderRequired: 'Please select a gender preference',
  competenceLevelRequired: 'Please select at least one competence level',
  onsiteFallbackRequired: 'Please specify how to handle cases when on-site interpreter is not found',
  interpreterConflict: (name) => `Interpreter ${name} cannot be both preferred and excluded`,
  invalidInterpreterId: 'One or more selected interpreters are invalid',
};

/**
 * PreferencesSection Component - Main container for interpreter preferences
 *
 * @param {Object} props
 * @param {boolean} [props.isOnsiteBooking=false] - Whether this is an on-site booking
 * @param {Object} [props.selectedLanguage=null] - Selected language
 * @param {Array} [props.alternativeLanguageObjects=[]] - Available alternative languages
 * @param {Object} [props.initialPreferences] - Initial preference values
 * @param {Function} [props.onChange] - Callback when preferences change
 * @param {Function} [props.onValidation] - Callback with validation state
 * @param {Function} [props.onSearchPreferred] - Optional custom search handler for preferred interpreters
 * @param {Function} [props.onSearchExcluded] - Optional custom search handler for excluded interpreters
 * @param {Array} [props.preferredSearchResults] - Optional external search results for preferred
 * @param {Array} [props.excludedSearchResults] - Optional external search results for excluded
 * @param {boolean} [props.preferredLoading] - Optional loading state for preferred search
 * @param {boolean} [props.excludedLoading] - Optional loading state for excluded search
 * @param {boolean} [props.showTitle=true] - Whether to show the internal "Preferences" title and two-column layout
 * @param {string} [props.className] - Additional CSS class
 * @param {Array} [props.levels=[]] - Authorization levels from backend (A-E)
 * @param {boolean} [props.useInterpreterTier=false] - Whether to show authorization levels
 * @param {Array} [props.skills=[]] - Skills from backend for current language (already filtered by parent)
 */
const PreferencesSection = ({
  isOnsiteBooking = false,
  isAnnouncementBooking = false,
  selectedLanguage = null,
  alternativeLanguageObjects = [],
  initialPreferences = defaultPreferences,
  onChange,
  onValidation,
  onSearchPreferred: externalSearchPreferred,
  onSearchExcluded: externalSearchExcluded,
  preferredSearchResults: externalPreferredResults,
  excludedSearchResults: externalExcludedResults,
  preferredLoading: externalPreferredLoading,
  excludedLoading: externalExcludedLoading,
  showTitle = true,
  className,
  levels = [],
  useInterpreterTier = false,
  skills = [],
  contractDistanceLimit = 0, // Distance limit from Contract (pbOnSiteKmLimit), 0 means no limit
}) => {
  // State
  const [preferences, setPreferences] = useState(initialPreferences);

  const [errors, setErrors] = useState({});

  // Additional preferences state
  const [additionalExpanded, setAdditionalExpanded] = useState(
    initialPreferences?.additionalPreferencesExpanded || false
  );

  // Search state for preferred interpreters (internal or external)
  const [internalPreferredResults, setInternalPreferredResults] = useState([]);
  const [internalPreferredLoading, setInternalPreferredLoading] = useState(false);

  // Search state for excluded interpreters (internal or external)
  const [internalExcludedResults, setInternalExcludedResults] = useState([]);
  const [internalExcludedLoading, setInternalExcludedLoading] = useState(false);

  // Use external or internal state
  const preferredSearchResults = externalPreferredResults !== undefined ? externalPreferredResults : internalPreferredResults;
  const excludedSearchResults = externalExcludedResults !== undefined ? externalExcludedResults : internalExcludedResults;
  const preferredLoading = externalPreferredLoading !== undefined ? externalPreferredLoading : internalPreferredLoading;
  const excludedLoading = externalExcludedLoading !== undefined ? externalExcludedLoading : internalExcludedLoading;

  // Update parent when preferences change
  useEffect(() => {
    if (onChange) {
      onChange(preferences);
    }
  }, [preferences, onChange]);

  // Validate and notify parent
  useEffect(() => {
    const isValid = validateAllFields();
    if (onValidation) {
      onValidation(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, onValidation]);

  // Handlers
  const handleGenderChange = (value) => {
    setPreferences(prev => ({ ...prev, gender: value }));
    clearError('gender');
  };

  const handleCompetenceLevelsChange = (values) => {
    // Handle mutual exclusivity for 'no-choice' option
    const prevValues = preferences.competenceLevels || [];
    const hadNoChoice = prevValues.includes('no-choice');
    const hasNoChoice = values.includes('no-choice');
    const otherValues = values.filter(v => v !== 'no-choice');

    let newValues;
    if (hasNoChoice && !hadNoChoice) {
      // User just selected no-choice -> clear all other selections
      newValues = ['no-choice'];
    } else if (otherValues.length > 0 && hasNoChoice) {
      // User selected another option while no-choice was selected -> remove no-choice
      newValues = otherValues;
    } else if (otherValues.length === 0 && !hasNoChoice) {
      // User deselected all options -> auto-select no-choice
      newValues = ['no-choice'];
    } else {
      newValues = values;
    }

    setPreferences(prev => ({ ...prev, competenceLevels: newValues }));
    clearError('competenceLevels');
  };

  const handleExperienceChange = (value) => {
    setPreferences(prev => ({ ...prev, experienceSkill: value }));
  };

  const handleOnsiteFallbackChange = (value) => {
    setPreferences(prev => ({ ...prev, onsiteFallbackHandling: value }));
    clearError('onsiteFallbackHandling');
  };

  const handleAcceptBeyondKmChange = (value) => {
    setPreferences(prev => ({
      ...prev,
      acceptBeyondKmLimit: value,
      // Reset kmBeyondLimit if user selects "No"
      kmBeyondLimit: value === false ? undefined : prev.kmBeyondLimit
    }));
    clearError('acceptBeyondKmLimit');
  };

  const handleKmBeyondLimitChange = (value) => {
    setPreferences(prev => ({ ...prev, kmBeyondLimit: value }));
    clearError('kmBeyondLimit');
  };

  const handleAcceptAlternativeLanguageChange = (value) => {
    setPreferences(prev => ({ ...prev, acceptAlternativeLanguage: value }));
    clearError('acceptAlternativeLanguage');
  };

  // Remote backup connection type handler
  const handleRemoteBackupCallTypeChange = (value) => {
    setPreferences(prev => ({ ...prev, backupCallTypeForOnSite: value }));
    clearError('backupCallTypeForOnSite');
  };

  // Additional preferences handlers
  const handleToggleAdditional = () => {
    setAdditionalExpanded(prev => !prev);
    setPreferences(prev => ({
      ...prev,
      additionalPreferencesExpanded: !additionalExpanded,
    }));
  };

  // Preferred interpreters handlers
  const handleSearchPreferred = useCallback((query) => {
    // Use external handler if provided
    if (externalSearchPreferred) {
      externalSearchPreferred(query);
      return;
    }

    // Otherwise use internal mock search
    if (query.length < 2) {
      setInternalPreferredResults([]);
      return;
    }

    setInternalPreferredLoading(true);

    // Mock search - in real implementation, this would call the API
    setTimeout(() => {
      const mockResults = [
        { id: '1', name: 'Anna Korhonen' },
        { id: '2', name: 'Mikko Virtanen' },
        { id: '3', name: 'Liisa Mäkinen' },
      ].filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );

      setInternalPreferredResults(mockResults);
      setInternalPreferredLoading(false);
    }, 300);
  }, [externalSearchPreferred]);

  const handleSelectPreferred = (interpreter) => {
    // Check if interpreter is in excluded list
    const isExcluded = preferences.excludedInterpreters?.some(
      item => item.id === interpreter.id
    );

    if (isExcluded) {
      setErrors(prev => ({
        ...prev,
        preferredInterpreters: validationMessages.interpreterConflict(interpreter.name),
      }));
      return;
    }

    // Add to preferred list
    setPreferences(prev => ({
      ...prev,
      preferredInterpreters: [...(prev.preferredInterpreters || []), interpreter],
    }));

    clearError('preferredInterpreters');
  };

  const handleRemovePreferred = (id) => {
    setPreferences(prev => ({
      ...prev,
      preferredInterpreters: (prev.preferredInterpreters || []).filter(
        item => item.id !== id
      ),
    }));
  };

  // Excluded interpreters handlers
  const handleSearchExcluded = useCallback((query) => {
    // Use external handler if provided
    if (externalSearchExcluded) {
      externalSearchExcluded(query);
      return;
    }

    // Otherwise use internal mock search
    if (query.length < 2) {
      setInternalExcludedResults([]);
      return;
    }

    setInternalExcludedLoading(true);

    // Mock search - in real implementation, this would call the API
    setTimeout(() => {
      const mockResults = [
        { id: '1', name: 'Anna Korhonen' },
        { id: '2', name: 'Mikko Virtanen' },
        { id: '3', name: 'Liisa Mäkinen' },
        { id: '4', name: 'Sari Nieminen' },
      ].filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );

      setInternalExcludedResults(mockResults);
      setInternalExcludedLoading(false);
    }, 300);
  }, [externalSearchExcluded]);

  const handleSelectExcluded = (interpreter) => {
    // Check if interpreter is in preferred list
    const isPreferred = preferences.preferredInterpreters?.some(
      item => item.id === interpreter.id
    );

    if (isPreferred) {
      setErrors(prev => ({
        ...prev,
        excludedInterpreters: validationMessages.interpreterConflict(interpreter.name),
      }));
      return;
    }

    // Add to excluded list
    setPreferences(prev => ({
      ...prev,
      excludedInterpreters: [...(prev.excludedInterpreters || []), interpreter],
    }));

    clearError('excludedInterpreters');
  };

  const handleRemoveExcluded = (id) => {
    setPreferences(prev => ({
      ...prev,
      excludedInterpreters: (prev.excludedInterpreters || []).filter(
        item => item.id !== id
      ),
    }));
  };

  // Filter skills by category
  const genderSkills = skills.filter(skill => skill.category?.tag === 'gender' || skill.category?.tag === 'hero-gen');

  // Filter experience skills (exclude dialect, gender, basic categories)
  const experienceSkills = skills.filter(skill =>
    !skill.category || (skill.category.tag !== 'dialect' && skill.category.tag !== 'gender' && skill.category.tag !== 'hero-gen' && skill.category.tag !== 'basic')
  );

  const experienceOptions = [
    { value: 'does_not_matter_experience', label: i18n('does_not_matter') },
    ...experienceSkills.map(skill => ({ value: skill.id, label: skill.name })),
  ];

  // Validation
  const validateAllFields = () => {
    const newErrors = {};

    // Gender validation - Only validate if there are gender skills available
    if (genderSkills.length > 0 && !preferences.gender) {
      newErrors.gender = validationMessages.genderRequired;
    }

    // Competence levels validation - Only validate if useInterpreterTier is enabled
    // 'no-choice' is valid for staging, so only validate if empty
    const shouldValidateCompetence = useInterpreterTier;
    if (shouldValidateCompetence && (!preferences.competenceLevels || preferences.competenceLevels.length === 0)) {
      newErrors.competenceLevels = validationMessages.competenceLevelRequired;
    }

    // Requirements validation - Not required, can be empty array or include 'no_requirements'
    // No validation needed as requirements are optional

    // On-site specific validation
    if (isOnsiteBooking && !preferences.onsiteFallbackHandling) {
      newErrors.onsiteFallbackHandling = validationMessages.onsiteFallbackRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  // Options for each preference category
  // Gender options: Fetch from backend skills with 'does not matter' as default
  const genderOptions = [
    { value: InterpreterGender.DOES_NOT_MATTER, label: i18n('does_not_matter') },
    ...genderSkills.map(skill => ({
      value: skill.id,
      label: skill.name,
    })),
  ];

  // DISABLE HERE - Toggle useInterpreterTier to hide/show authorization levels (A-E)
  // Competence level options: Authorization levels from backend (Level A, B, C, D, E)
  // Only shown when useInterpreterTier is enabled for the business type
  const competenceOptions = useInterpreterTier ? [
    ...levels.map(level => ({
      value: level.id,
      interpreterTier: level.interpreterTier,
      label: level.name || `${i18n('interpreter_level_label')} ${level.levelLabel}`,
    }))
  ] : [];

  const fallbackOptions = [
    { value: OnsiteFallbackHandling.ASSIGN_REMOTE, label: i18n('preferences_fallback_assign_remote', 'Assign remote interpreter') },
    { value: OnsiteFallbackHandling.CANCEL_BOOKING, label: i18n('preferences_fallback_cancel', 'Cancel booking') },
  ];

  const acceptBeyondKmOptions = [
    { value: true, label: i18n('yes', 'Yes') },
    { value: false, label: i18n('no', 'No') },
  ];

  const kmBeyondValueOptions = [
    { value: 10, label: '10 km' },
    { value: 20, label: '20 km' },
    { value: 30, label: '30 km' },
    { value: 40, label: '40 km' },
    { value: 50, label: '50 km' },
    { value: 70, label: '70 km' },
    { value: 'unlimited', label: i18n('unlimited') },
  ];

  const alternativeLanguageOptions = [
    { value: true, label: i18n('yes', 'Yes') },
    { value: false, label: i18n('no', 'No') },
  ];

  const formatLanguageList = (arr) => {
    if (!arr || arr.length === 0) {
      return '';
    }
    if (arr.length === 1) {
      return arr[0];
    }
    if (arr.length === 2) {
      return arr.join(` ${i18n('or', 'or')} `);
    }
    return `${arr.slice(0, -1).join(', ')} ${i18n('or', 'or')} ${arr[arr.length - 1]}`;
  };

  const hasAlternativeLanguage = alternativeLanguageObjects && alternativeLanguageObjects.length > 0;
  const altLanguageName = hasAlternativeLanguage ? formatLanguageList(alternativeLanguageObjects.map(l => l.name)) : '';
  const template = i18n('preferences_alternative_language_fallback', 'If a {0} speaking interpreter is not available, can we assign a {1} speaking interpreter?');
  const altLanguageLabel = selectedLanguage ? template.replace('{0}', selectedLanguage.name).replace('{1}', altLanguageName) : '';

  // Render the settings content
  const renderSettingsContent = () => (
    <>
      {/* Alternative Language Fallback */}
      {hasAlternativeLanguage && (
        <>
          <PreferenceGroup
            id="alternativeLanguageFallback"
            label={altLanguageLabel}
            options={alternativeLanguageOptions}
            selectedValues={preferences.acceptAlternativeLanguage}
            onChange={handleAcceptAlternativeLanguageChange}
            error={errors.acceptAlternativeLanguage}
          />

          <Divider />
        </>
      )}

      {/* Interpreter Gender - Only shown if gender skills are available and not announcement booking */}
      {genderSkills.length > 0 && !isAnnouncementBooking && (
        <>
          <PreferenceGroup
            id="gender"
            label={i18n('preferences_interpreter_gender', 'Interpreter gender')}
            options={genderOptions}
            selectedValues={preferences.gender}
            onChange={handleGenderChange}
            error={errors.gender}
            required
          />

          <Divider />
        </>
      )}

      {/* Competence Level (Authorization Levels A-E) - Conditional on useInterpreterTier */}
      {useInterpreterTier && (!competenceOptions?.length > 0) && (
        <ErrorMessage role="alert">
          {i18n('competence_level_initialization_error', 'Competence levels missing. Please contact support.')}
        </ErrorMessage>
      )}
      {useInterpreterTier && competenceOptions?.length > 0 && (
        <>
          <PreferenceGroup
            id="competence"
            label={i18n('competence_level_special_need', 'Competence level/special need for interpreter')}
            helperText={i18n('competence_level_helper_text')}
            options={competenceOptions}
            selectedValues={preferences.competenceLevels}
            multiSelect
            onChange={handleCompetenceLevelsChange}
            error={errors.competenceLevels}
          />

          {/* Show help text when 'no-choice' is selected (staging only) */}
          {isHeroBrandEnabled() && preferences.competenceLevels?.includes('no-choice') && (
            <HelpText>
              {i18n('let_system_choose_highest_qualified', 'Let the system choose the highest qualified interpreter')}
            </HelpText>
          )}

          <Divider />
        </>
      )}

      {/* Experiences (filtered skills) */}
      {experienceSkills.length > 0 && (
        <>
        <PreferenceGroup
          id="experience"
          label={i18n('experiences')}
          options={experienceOptions}
          selectedValues={preferences.experienceSkill}
          onChange={handleExperienceChange}
        />
        <Divider />
        </>
      )}

      {/* On-site specific fields */}
      {isOnsiteBooking && (
        <>
          {/* Distance extension question - asked first when there is a contractual limit */}
          {contractDistanceLimit > 0 && (
            <>
              <PreferenceGroup
                id="acceptBeyondKm"
                label={`${i18n('preferences_accept_beyond_km').replace('{}', contractDistanceLimit)}`}
                options={acceptBeyondKmOptions}
                selectedValues={preferences.acceptBeyondKmLimit}
                onChange={handleAcceptBeyondKmChange}
              />

              {/* Show km beyond field only when user accepts distance limit */}
              {preferences.acceptBeyondKmLimit === true && (
                <KmBeyondFieldContainer>
                  <Label
                    text={`${i18n('preferences_km_beyond_limit').replace('{}', contractDistanceLimit)}`}
                  />
                  <KmBeyondSelect
                    value={preferences.kmBeyondLimit ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        handleKmBeyondLimitChange(undefined);
                      } else if (v === 'unlimited') {
                        handleKmBeyondLimitChange('unlimited');
                      } else {
                        handleKmBeyondLimitChange(Number(v));
                      }
                    }}
                  >
                    <option value="" disabled>
                      {i18n('select', 'Select')}
                    </option>
                    {kmBeyondValueOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </KmBeyondSelect>
                  <KmBeyondInfo>
                    {preferences.kmBeyondLimit === 'unlimited'
                      ? i18n('preferences_total_max_distance_unlimited')
                      : i18n('preferences_total_max_distance_label_one_way').replace(
                          '{}',
                          contractDistanceLimit + (Number(preferences.kmBeyondLimit) || 0)
                        )}
                  </KmBeyondInfo>
                </KmBeyondFieldContainer>
              )}

              <Divider />
            </>
          )}

          <PreferenceGroup
            id="onsiteFallback"
            label={i18n('preferences_onsite_fallback', 'How would like us to handle case interpreter is not found for on-site?')}
            options={fallbackOptions}
            selectedValues={preferences.onsiteFallbackHandling}
            onChange={handleOnsiteFallbackChange}
            error={errors.onsiteFallbackHandling}
            required
          />

          {/* Remote backup connection type - Only shown when assign_remote_interpreter is selected */}
          {preferences.onsiteFallbackHandling === OnsiteFallbackHandling.ASSIGN_REMOTE && (
            <>
              <Divider />
              <PreferenceGroup
                id="backupCallTypeForOnSite"
                label={i18n('preferences_remote_backup_connection_type', 'Preferred connection type for remote backup')}
                options={[
                  { value: 'call', label: i18n('preferences_remote_backup_phone', 'Phone call') },
                  // Only show video option for non-Hero brand
                  ...(isHeroBrandEnabled() ? [] : [{ value: 'data', label: i18n('preferences_remote_backup_video', 'Video call') }]),
                  // Conference call option
                  { value: 'conference', label: i18n('preferences_remote_backup_conference', 'Conference call') },
                ]}
                selectedValues={preferences.backupCallTypeForOnSite}
                onChange={handleRemoteBackupCallTypeChange}
              />
            </>
          )}
        </>
      )}

      {/* Additional Preferences Section */}
      <AdditionalPreferencesSection
        expanded={additionalExpanded}
        onToggle={handleToggleAdditional}
        preferredInterpreters={preferences.preferredInterpreters || []}
        excludedInterpreters={preferences.excludedInterpreters || []}
        onSearchPreferred={handleSearchPreferred}
        onSelectPreferred={handleSelectPreferred}
        onRemovePreferred={handleRemovePreferred}
        onSearchExcluded={handleSearchExcluded}
        onSelectExcluded={handleSelectExcluded}
        onRemoveExcluded={handleRemoveExcluded}
        preferredSearchResults={preferredSearchResults}
        excludedSearchResults={excludedSearchResults}
        preferredLoading={preferredLoading}
        excludedLoading={excludedLoading}
        preferredError={errors.preferredInterpreters}
        excludedError={errors.excludedInterpreters}
      />
    </>
  );

  return (
    <SectionContainer className={className} $showBorder={showTitle}>
      {showTitle ? (
        <ContentLayout>
          <TitleColumn>
            <Title>{i18n('preferences_title', 'Preferences')}</Title>
          </TitleColumn>
          <SettingsColumn>
            {renderSettingsContent()}
          </SettingsColumn>
        </ContentLayout>
      ) : (
        renderSettingsContent()
      )}
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  /* Layout */
  background: ${({ $showBorder }) => $showBorder ? '#ffffff' : 'transparent'};
  border: ${({ $showBorder }) => $showBorder ? '1px solid rgba(69, 68, 107, 0.2)' : 'none'};
  border-radius: ${({ $showBorder }) => $showBorder ? '8px' : '0'};
  padding: ${({ $showBorder }) => $showBorder ? '24px' : '0'};

  /* Box shadow for depth (optional) */
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); */
`;

const ContentLayout = styled.div`
  /* Horizontal layout */
  display: flex;
  gap: 48px;

  /* Responsive: Stack vertically on mobile */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const TitleColumn = styled.div`
  /* Fixed width on desktop */
  width: 160px;
  flex-shrink: 0;

  /* Responsive */
  @media (max-width: 1024px) {
    width: 120px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Title = styled.h2`
  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: 0.5px;
  color: #45446b;

  /* Layout */
  margin: 0;
  padding: 0;
`;

const SettingsColumn = styled.div`
  /* Flexible width */
  flex: 1;

  /* Layout */
  display: flex;
  flex-direction: column;
`;

const KmBeyondFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  margin-top: 20px;
`;

const KmBeyondSelect = styled.select`
  background-color: #f6f8fb;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin: 6px 0;
  padding: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: #45446b;
  letter-spacing: 0.5px;
  cursor: pointer;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10l5 5 5-5' stroke='%2345446b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 24px;
  padding-right: 40px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    color: #45446b;
    background-color: #ffffff;
  }

  option:disabled {
    color: rgba(69, 68, 107, 0.6);
  }
`;

const KmBeyondInfo = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: #508f92;
  letter-spacing: 0.15px;
  padding-left: 8px;
  margin: 0;
`;

const HelpText = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.4;
  color: #508f92;
  letter-spacing: 0.15px;
  margin: 8px 0 0 0;
  padding: 0;
`;

const ErrorMessage = styled.div`
  /* Typography */
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: #e03333;

  /* Layout */
  margin-top: 8px;
  padding-left: 8px;
`;

export default PreferencesSection;
